import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { CopyIcon } from '@rsos/assets/icons';
import { BE_END_CALL } from '@rsos/constants/callStatuses';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { trackTranscriptionTabCopy } from '../../../../../../../gainsightTrackings/callTranscriptionTrackings';
import type { TranscriptionStateToken } from '../../transcriptionSlice/transcriptionSlice.types';
import CopyButton from '../IconButton';
import { formatTimestamp } from '../TranscriptViewer';

interface TranscriptionPayload {
  tokens?: TranscriptionStateToken[];
}

interface Transcription {
  payload?: TranscriptionPayload;
}

interface CopyTranscriptProps {
  transcription: Transcription;
  selectedTranscript: 'original' | 'translated';
}

const formatCopyText = (
  tokens: TranscriptionStateToken[],
  selectedTranscript: 'original' | 'translated' = 'original',
): string[] => {
  const getMessage = (token: TranscriptionStateToken): string => {
    if (!token) return '';

    const { speaker_display_name, start_time } = token;

    let transcript = token?.transcript;
    if (selectedTranscript === 'translated' && token?.translated) {
      transcript = token?.translated;
    }

    return `${speaker_display_name} ${formatTimestamp(
      start_time,
    )}:\n"${transcript}"`;
  };

  return tokens.map(getMessage).filter(Boolean);
};

const CopyTranscript = ({
  transcription,
  selectedTranscript,
}: CopyTranscriptProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const { tokens = [] } = transcription?.payload || {};

  const callStateRef = useRef<string | null>(null);

  const callerID = useSelector(
    (state: StateWithEmergencyCalls) =>
      state.emergencyCalls.selectedCallerID || '',
  );
  const callDetails = useSelector(
    (state: StateWithEmergencyCalls) =>
      state.emergencyCalls.callQueue[callerID],
  );

  // Track the call state at the time the user selected the Transcription Tab.
  useEffect(() => {
    if (
      callStateRef.current === null &&
      callerID &&
      callDetails?.callStatuses
    ) {
      callStateRef.current =
        callDetails?.callStatuses?.at(-1) === BE_END_CALL ? 'ended' : 'active';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callerID, callDetails?.callStatuses]);

  const handleClick = () => {
    if (!tokens.length) {
      return;
    }

    if (!navigator.clipboard) {
      // eslint-disable-next-line no-console -- browser capability not available
      console.error(
        'Copy feature unavailable. It is only available on a secure (HTTPS) connection.',
      );
      return;
    }
    const text = formatCopyText(tokens, selectedTranscript).join('\n\n');
    navigator.clipboard.writeText(text);

    setIsCopied(true);
    trackTranscriptionTabCopy({
      copyType:
        selectedTranscript === 'original' ? 'full_transcript' : 'translation',
      callState: callStateRef.current || 'unknown',
    });
    window.setTimeout(() => {
      setIsCopied(false);
    }, 3000);
  };

  const isDisabled = tokens.length === 0;
  const tooltip = isDisabled
    ? 'Copy is available after transcript starts.'
    : isCopied
      ? 'Copied'
      : 'Copy All';

  return (
    <CopyButton
      dataName="transcript-copy-button"
      disabled={isDisabled}
      size="small"
      onClick={handleClick}
      tooltip={tooltip}
    >
      <CopyIcon height={12} width={12} />
    </CopyButton>
  );
};

export default CopyTranscript;
